// src/router/index.ts
import { createRouter, createWebHistory } from "vue-router";
import { allRoutes } from "@/router/routes";
import { checkLoginStatus } from "@/helpers/api/auth";
import { fetchServerTierById } from "@/helpers/api/server";
import { getPanelDetails } from "@/helpers/api/panel";
import { getApplicationDetails } from "@/helpers/api/application"; // 🟢 NEW
import { useContextStore } from "@/stores/context";
import { useNotifications } from "@/components/useNotifications";
import { invalidateCache, invalidateCachePattern } from "@/helpers/apiCache";
import { watch } from "vue";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { appName, appTitle } from "@/helpers";

function redirect(from: RouteLocationNormalized, location: RouteLocationRaw) {
  return { ...(location as object), replace: !from.name } as RouteLocationRaw;
}

declare global {
  interface Window {
    HSStaticMethods: any;
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: allRoutes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: "smooth" };
    return { left: 0, top: 0 };
  },
});

// --- Utility: Safely extract params/queries ---
const getParam = (val: any): string | undefined => {
  if (!val) return undefined;
  if (Array.isArray(val)) return val[0] ? String(val[0]) : undefined;
  return String(val);
};

// --- 1. Hydration Gate ---
router.beforeEach(async (to) => {
  const contextStore = useContextStore();
  if (!contextStore.isHydrated) {
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => { unwatch(); resolve(); }, 3000);
      const unwatch = watch(() => contextStore.isHydrated, (v) => {
        if (v) { clearTimeout(timeout); unwatch(); resolve(); }
      });
    });
  }
  return true;
});

// --- 2. Page Title Management ---
router.beforeEach((to) => {
  // If we're on the landing page, use the full SEO title
  if (to.name === "landing") {
    document.title = appTitle;
    return true;
  }

  // Otherwise, find the nearest section/page title in the hierarchy
  const matchedRecord = [...to.matched].reverse().find((r) => r.meta?.title);
  const sectionTitle = matchedRecord?.meta?.title as string | undefined;

  // Formats as "TicketForge | Dashboard", "TicketForge | Admin", etc.
  // Falls back to appTitle if an unknown untitle route is loaded
  document.title = sectionTitle ? `${appName} | ${sectionTitle}` : appTitle;
  return true;
});

// --- 3. Preline UI Auto-Init ---
router.afterEach((to, from, failure) => {
  if (!failure) {
    setTimeout(() => {
      if (window.HSStaticMethods && typeof window.HSStaticMethods.autoInit === "function") {
        window.HSStaticMethods.autoInit();
      }
    }, 100);
  }
});

// --- 4. Context Clearing Guard ---
router.beforeEach((to) => {
  const contextStore = useContextStore();
  const globalContextRoutes = ["pages.servers"];

  if (to.name && globalContextRoutes.includes(to.name as string)) {
    if (contextStore.hasServerSelected) {
      contextStore.clearServerSelection();
    }
  }
  return true;
});

// --- 5. Authentication Guard ---
router.beforeEach(async (to, from) => {
  const requiresAuth = to.matched.some((route) => route.meta.requiresAuth);
  if (!requiresAuth) return true;

  const contextStore = useContextStore();
  if (contextStore.isLoggedIn) return true;

  try {
    const isLoggedIn = await checkLoginStatus(true);
    if (isLoggedIn) return true;

    const { addNotification } = useNotifications();
    if (from.name !== undefined) {
      addNotification({
        type: "warning",
        title: "Session Expired",
        message: "Please log in again to continue.",
        duration: 4000,
      });
    }

    return { name: "auth.login", query: { redirect: to.fullPath } };
  } catch (error) {
    console.error("[Router Guard] Auth check failed:", error);
    return { name: "auth.login", query: { redirect: to.fullPath } };
  }
});

// --- 6. Server Access Authorization Guard ---
router.beforeEach(async (to, from) => {
  const toGuildId = getParam(to.params.guildId) || getParam(to.query.guildId);
  if (!toGuildId) return true;

  const fromGuildId = getParam(from.params.guildId) || getParam(from.query.guildId);
  if (toGuildId === fromGuildId) return true;

  const contextStore = useContextStore();
  const { addNotification } = useNotifications();

  try {
    if (to.matched.some((route) => route.meta?.skipServerAccessCheck)) {
      return true;
    }

    if (contextStore.user?.adminLevel) return true;

    const isHardLoad = !from.name;
    const userServers = await contextStore.getOrFetchUserServers(isHardLoad);
    const hasGuildAccess = !!userServers.guildsWithBot?.some((g) => g.id === toGuildId);

    if (!hasGuildAccess) {
      addNotification({
        type: "error",
        title: "Access Denied",
        message: "You do not have permission to manage this server, or the bot is missing.",
        duration: 5000,
      });
      contextStore.clearServerSelection();
      return redirect(from, { name: "pages.servers", query: {} });
    }

  } catch (error) {
    console.error("[Router Guard] Server access check failed:", error);
    contextStore.clearServerSelection();
    return redirect(from, { name: "pages.servers", query: {} });
  }

  return true;
});

// --- 7. Admin Server Context Guard ---
router.beforeEach((to, from) => {
  const requiresServerSelection = to.matched.some((route) => route.meta?.requiresServerSelection);
  if (!requiresServerSelection) return true;

  const contextStore = useContextStore();
  if (!contextStore.hasServerSelected) {
    return redirect(from, { name: "pages.servers", query: {} });
  }
  return true;
});

// --- 8. URL Context Synchronization & Hydration Guard ---
router.beforeEach((to) => {
  const contextStore = useContextStore();
  const guildId = getParam(to.params.guildId) || getParam(to.query.guildId);
  const panelId = getParam(to.params.panelId) || getParam(to.query.panelId);
  const applicationId = getParam(to.params.applicationId) || getParam(to.query.applicationId);

  // A. Sync Guild ID
  if (guildId && !to.matched.some((route) => route.meta?.skipServerAccessCheck)) {
    const needsHydration = contextStore.currentServer?.id !== guildId || contextStore.currentServer?.name === "Loading...";

    if (needsHydration) {
      contextStore.selectServer({
        id: guildId,
        name: contextStore.currentServer?.id === guildId ? contextStore.currentServer.name : "Loading...",
        avatarUrl: contextStore.currentServer?.avatarUrl,
      });

      if (contextStore.isLoggedIn) {
        contextStore.getOrFetchUserServers().then((data) => {
          const found = data.guildsWithBot.find((g) => g.id === guildId);
          if (found) {
            contextStore.selectServer({
              id: found.id,
              name: found.name,
              avatarUrl: found.avatarUrl || undefined,
            });
          }
        }).catch(() => { });
      }
    }
  }

  // B. Sync Panel ID
  if (panelId) {
    if (contextStore.currentPanel?.id !== panelId) {
      contextStore.selectPanel({
        id: panelId,
        name: "Loading...",
        serverId: guildId || contextStore.serverId || "",
      });
    }
  } else if (contextStore.hasPanelSelected) {
    contextStore.clearPanelSelection();
  }

  // C. Sync Application ID
  if (applicationId) {
    if (contextStore.currentApplication?.id !== applicationId) {
      contextStore.selectApplication({
        id: applicationId,
        name: "Loading...",
        serverId: guildId || contextStore.serverId || "",
      });
    }
  } else if (contextStore.hasApplicationSelected) {
    contextStore.clearApplicationSelection();
  }

  return true;
});

// --- 9. Premium Panel/Application Access Guard ---
// 🟢 PARITY FIX: Application integration and cache invalidation added.
router.beforeEach(async (to, from) => {
  if (to.name !== "pages.panel.edit" && to.name !== "pages.application.edit") return true;

  const isApp = to.name === "pages.application.edit";
  const toGuildId = getParam(to.params.guildId);
  const toId = getParam(to.params.panelId) || getParam(to.params.applicationId);

  if (!toGuildId || !toId) return true;

  // 🚀 SPEED OPTIMIZATION: Skip API verification if just changing query params
  const fromGuildId = getParam(from.params.guildId);
  const fromId = getParam(from.params.panelId) || getParam(from.params.applicationId);
  if (from.name === to.name && toGuildId === fromGuildId && toId === fromId) {
    return true;
  }

  const contextStore = useContextStore();
  const { addNotification } = useNotifications();

  try {
    let isPremium = false;
    
    if (isApp) {
      const appData = await getApplicationDetails(toGuildId, toId);
      isPremium = appData?.isPremium;
    } else {
      const panelData = await getPanelDetails(toGuildId, toId);
      isPremium = panelData?.isPremium;
    }

    if (!isPremium) return true;

    // Hard refresh server tier on initial load to prevent spoofing
    contextStore.setServerTierStatus(toGuildId, "loading");
    const serverIsPremium = await fetchServerTierById(toGuildId);
    contextStore.setServerTierStatus(toGuildId, "success", serverIsPremium);

    if (serverIsPremium) return true;

    addNotification({
      type: "warning",
      title: "Premium Required",
      message: `This ${isApp ? 'application' : 'panel'} requires an active premium subscription.`,
      duration: 4000,
    });
    return redirect(from, { name: "pages.premium", params: { guildId: toGuildId } });

  } catch (error: any) {
    console.error(`[Router Guard] Premium ${isApp ? 'application' : 'panel'} access check failed:`, error);

    const isNotFound =
      error?.status === 404 ||
      error?.response?.status === 404 ||
      error?.message?.toLowerCase().includes("not found");

    if (isNotFound) {
      if (isApp) {
        invalidateCache(`app:${toGuildId}:${toId}`);
        invalidateCachePattern(`applications:${toGuildId}`);
        contextStore.clearApplicationSelection();
      } else {
        invalidateCache(`panel:${toGuildId}:${toId}`);
        invalidateCachePattern(`panels:${toGuildId}`);
        contextStore.clearPanelSelection();
      }

      addNotification({
        type: "warning",
        title: "Not Found",
        message: `This ${isApp ? 'application' : 'panel'} no longer exists or was deleted.`,
        duration: 4000,
      });

      return redirect(from, {
        name: isApp ? "pages.applications" : "pages.panels",
        params: { guildId: toGuildId },
        query: { refresh: Date.now().toString() },
      });
    }

    // Fallback for actual server errors / 500s during premium checks
    addNotification({
      type: "warning",
      title: "Access Restricted",
      message: "Unable to verify premium access. Please check Premium for this server.",
      duration: 5000,
    });
    return redirect(from, { name: "pages.premium", params: { guildId: toGuildId } });
  }
});

// --- 10. Admin Authorization Guard ---
router.beforeEach((to, from) => {
  const requiresAdmin = to.matched.some((route) => route.meta.requiresAdmin);
  if (!requiresAdmin) return true;

  const contextStore = useContextStore();
  const userLevel = contextStore.user?.adminLevel;

  if (!userLevel) {
    const { addNotification } = useNotifications();
    addNotification({
      type: "error",
      title: "Forbidden",
      message: "You do not have administrative privileges.",
    });
    return redirect(from, { name: "pages.servers" });
  }

  if (to.meta.requiredAdminLevels) {
    const allowedLevels = to.meta.requiredAdminLevels as string[];

    if (!allowedLevels.includes(userLevel)) {
      const { addNotification } = useNotifications();
      addNotification({
        type: "error",
        title: "Access Denied",
        message: "Your admin tier does not have permission to view this page.",
      });
      return redirect(from, { name: "moderation.review" });
    }
  }

  return true;
});

export default router;